// Hook React per gestione PWA offline
import { useState, useEffect } from 'react';

interface OfflineState {
  isOnline: boolean;
  isPWAInstalled: boolean;
  serviceWorkerReady: boolean;
  cacheStatus: 'loading' | 'ready' | 'error' | 'empty';
}

export const useOffline = () => {
  const [state, setState] = useState<OfflineState>({
    isOnline: navigator.onLine,
    isPWAInstalled: false,
    serviceWorkerReady: false,
    cacheStatus: 'empty'
  });

  useEffect(() => {
    // In locale non registriamo il SW (evita cache stantie)
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!isLocalhost) {
      registerServiceWorker();
    }
    
    // Ascolta cambi connessione
    const handleOnline = () => setState(prev => ({ ...prev, isOnline: true }));
    const handleOffline = () => setState(prev => ({ ...prev, isOnline: false }));
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Controlla se PWA è installata
    checkPWAInstalled();
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setup una tantum al mount
  }, []);

  const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('[PWA] Service Worker registered:', registration);
        
        setState(prev => ({ ...prev, serviceWorkerReady: true }));
        
        // Verifica cache al caricamento
        checkCacheStatus();
        
      } catch (error) {
        console.error('[PWA] Service Worker registration failed:', error);
        setState(prev => ({ ...prev, serviceWorkerReady: false }));
      }
    }
  };

  const checkPWAInstalled = () => {
    // Controlla se l'app è in modalità standalone (installata)
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                  (window.navigator as any).standalone ||
                  document.referrer.includes('android-app://');
    
    setState(prev => ({ ...prev, isPWAInstalled: isPWA }));
  };

  const checkCacheStatus = async () => {
    if (!('caches' in window)) return;
    
    try {
      setState(prev => ({ ...prev, cacheStatus: 'loading' }));
      
      const cacheNames = await caches.keys();
      const ricetteCache = cacheNames.find(name => name.includes('ricette'));
      
      if (ricetteCache) {
        const cache = await caches.open(ricetteCache);
        const keys = await cache.keys();
        
        // Controlla se abbiamo i 3 endpoint delle ricette
        const hasGusti = keys.some(req => req.url.includes('/api/gusti'));
        const hasRicette = keys.some(req => req.url.includes('/api/ricette-gusti'));
        const hasIngredienti = keys.some(req => req.url.includes('/api/ingredienti'));
        
        if (hasGusti && hasRicette && hasIngredienti) {
          setState(prev => ({ ...prev, cacheStatus: 'ready' }));
        } else {
          setState(prev => ({ ...prev, cacheStatus: 'empty' }));
        }
      } else {
        setState(prev => ({ ...prev, cacheStatus: 'empty' }));
      }
      
    } catch (error) {
      console.error('[PWA] Error checking cache:', error);
      setState(prev => ({ ...prev, cacheStatus: 'error' }));
    }
  };

  const preloadRicetteData = async () => {
    if (!state.serviceWorkerReady) return false;
    
    try {
      setState(prev => ({ ...prev, cacheStatus: 'loading' }));
      
      // Invia messaggio al service worker per precaricare
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_RICETTE'
        });
      }
      
      // Aspetta un po' e ricontrolla
      setTimeout(() => {
        checkCacheStatus();
      }, 2000);
      
      return true;
    } catch (error) {
      console.error('[PWA] Error preloading ricette:', error);
      setState(prev => ({ ...prev, cacheStatus: 'error' }));
      return false;
    }
  };

  const clearCache = async () => {
    if (!state.serviceWorkerReady) return;
    
    try {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_CACHE'
        });
      }
      
      setState(prev => ({ ...prev, cacheStatus: 'empty' }));
    } catch (error) {
      console.error('[PWA] Error clearing cache:', error);
    }
  };

  return {
    ...state,
    preloadRicetteData,
    clearCache,
    checkCacheStatus
  };
};
